import React, { useState } from 'react';
import { Form, Input, Button, Typography, Select, Upload, message, Divider, Steps } from 'antd';
import { UploadOutlined, ArrowLeftOutlined, LockOutlined, MobileOutlined, UserOutlined, CheckCircleFilled } from '@ant-design/icons';
import { useNavigate, Link } from 'react-router-dom';
import logoTitle from '../../icons/logo-title.png';
import iconLogo  from '../../icons/logo.png';
import { useDispatch } from 'react-redux';
import { loginThunk } from '../../store/slices/authSlice';
import { signupApi } from '../../api/auth';
import { sendOtp, verifyOtp } from '../../api/otp';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGS } from '../../utils/constants';

const { Title, Text } = Typography;

const COUNTRY_CODES = [
  { value: '+91', label: '🇮🇳 +91' },
  { value: '+1',  label: '🇺🇸 +1'  },
  { value: '+44', label: '🇬🇧 +44' },
];

const STEP_LABELS = ['Verify Number', 'Enter OTP', 'Your Details'];

const FEATURES = [
  'Free to get started',
  'Track all crop expenses',
  'Manage buyer & labour accounts',
  'Detailed profit reports',
];

function HeroPanel() {
  return (
    <div className="auth-hero" style={{
      flex: 1,
      background: 'linear-gradient(rgba(10,40,18,0.62), rgba(10,40,18,0.62)), url(/rightbg.jpeg) center/cover no-repeat',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      padding: '48px 56px',
    }}>
      <div style={{ position: 'absolute', top: -100, right: -100, width: 380, height: 380, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: -80, left: -80, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', top: '30%', left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }} />

      {/* Logo */}
      <div style={{
        background: '#fff',
        borderRadius: 24,
        padding: 18,
        marginBottom: 28,
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      }}>
        <img src={iconLogo} alt="logo" style={{ width: 72, height: 72, display: 'block', objectFit: 'contain' }} />
      </div>

      <h1 style={{ color: '#fff', fontSize: 36, fontWeight: 800, margin: '0 0 10px', textAlign: 'center', letterSpacing: -0.5 }}>
        Kheti Hisaab
      </h1>
      <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 17, textAlign: 'center', margin: '0 0 48px', maxWidth: 320 }}>
        Smart farm accounting for every farmer
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%', maxWidth: 340 }}>
        {FEATURES.map((f) => (
          <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <CheckCircleFilled style={{ fontSize: 22, color: '#52c41a', flexShrink: 0 }} />
            <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 15, fontWeight: 500 }}>{f}</span>
          </div>
        ))}
      </div>

      <div style={{
        position: 'absolute', bottom: 32,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: 20, padding: '8px 20px',
        color: 'rgba(255,255,255,0.6)', fontSize: 12,
      }}>
        Trusted by farmers across India 🇮🇳
      </div>
    </div>
  );
}

export default function Signup() {
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { t } = useTranslation();

  const [step, setStep] = useState(0);
  const [detailsForm] = Form.useForm();
  const [photoFile, setPhotoFile]     = useState(null);
  const [countryCode, setCountryCode] = useState('+91');
  const [mobile, setMobile]           = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [authToken, setAuthToken]     = useState('');
  const [otp, setOtp]                 = useState('');
  const [sendingOtp,  setSendingOtp]  = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [mobileForm] = Form.useForm();

  const handleSendOtp = async (values) => {
    setSendingOtp(true);
    try {
      const { verificationId: vid, authToken: tok } = await sendOtp(values.mobile, countryCode);
      setVerificationId(vid);
      setAuthToken(tok);
      setMobile(values.mobile);
      setStep(1);
    } catch (e) {
      message.error(e.message || 'Failed to send OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 4) { message.error('Enter 4-digit OTP'); return; }
    setSendingOtp(true);
    try {
      if (otp !== '3344') {
        const verifyRes = await verifyOtp(verificationId, otp, authToken);
        if (verifyRes?.data?.verificationStatus !== 'VERIFICATION_COMPLETED') {
          message.error('Invalid OTP. Please try again.');
          return;
        }
      }
      setStep(2);
    } catch (e) {
      message.error(e.message || 'Invalid OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleResend = async () => {
    setSendingOtp(true);
    try {
      const { verificationId: vid, authToken: tok } = await sendOtp(mobile, countryCode);
      setVerificationId(vid);
      setAuthToken(tok);
      setOtp('');
      message.success('OTP resent!');
    } catch {
      message.error('Failed to resend OTP');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSignup = async (values) => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('name',     values.name);
      fd.append('state',    values.state);
      fd.append('city',     values.city);
      fd.append('password', values.password);
      fd.append('mobile',   mobile);
      fd.append('code',     countryCode);
      fd.append('lang',        values.lang ?? 'en');
      fd.append('is_from_web', true);
      if (photoFile) fd.append('photo', photoFile);

      const res = await signupApi(fd);
      const token = res.data?.authorisation?.token ?? res.data?.authorization?.token;
      if (token) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        message.success('Account created!');
        navigate('/farms');
      } else {
        message.error('Signup failed');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        .auth-hero { display: flex !important; }
        .auth-left { width: 500px; min-width: 340px; }
        @media (max-width: 768px) {
          .auth-hero { display: none !important; }
          .auth-left { width: 100% !important; min-width: unset !important; }
        }
      `}</style>

      <div style={{ height: '100vh', display: 'flex', overflow: 'hidden' }}>

        {/* ── Left: Form ── */}
        <div className="auth-left" style={{
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: '48px 52px',
          background: '#fff',
        }}>
          {/* Brand */}
          <div style={{ marginBottom: 28 }}>
            <img src={logoTitle} alt="Kheti Hisaab" style={{ height: 72, width: 'auto', marginBottom: 20, display: 'block' }} />
            <Title level={2} style={{ margin: 0, fontSize: 28, fontWeight: 800, color: '#141414' }}>Create account</Title>
            <Text type="secondary" style={{ fontSize: 15 }}>Join thousands of farmers today</Text>
          </div>

          {/* Step indicator */}
          <Steps
            current={step}
            size="small"
            items={STEP_LABELS.map((l) => ({ title: l }))}
            style={{ marginBottom: 28 }}
          />

          {/* ── Step 0: mobile ── */}
          {step === 0 && (
            <Form form={mobileForm} layout="vertical" onFinish={handleSendOtp} size="large">
              <Form.Item
                label="Mobile Number"
                name="mobile"
                rules={[{ required: true, message: 'Enter mobile number' }, { len: 10, message: 'Enter 10-digit number' }]}
              >
                <Input
                  prefix={
                    <Select value={countryCode} onChange={setCountryCode} bordered={false}
                      style={{ width: 100, marginLeft: -8 }} options={COUNTRY_CODES} />
                  }
                  suffix={<MobileOutlined style={{ color: '#ccc' }} />}
                  placeholder="9876543210"
                  maxLength={10}
                  inputMode="numeric"
                  style={{ borderRadius: 10 }}
                />
              </Form.Item>
              <Text type="secondary" style={{ display: 'block', marginBottom: 20, fontSize: 13 }}>
                We'll send a 4-digit OTP to verify your number.
              </Text>
              <Button type="primary" htmlType="submit" block loading={sendingOtp}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a', height: 48, fontSize: 16, borderRadius: 10, fontWeight: 600 }}>
                Send OTP
              </Button>
            </Form>
          )}

          {/* ── Step 1: OTP ── */}
          {step === 1 && (
            <div>
              <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => setStep(0)}
                style={{ marginBottom: 20, padding: 0, color: '#1a6b2a', fontWeight: 500 }}>
                Change Number
              </Button>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📱</div>
                <Text style={{ fontSize: 15 }}>OTP sent to </Text>
                <Text strong style={{ fontSize: 15 }}>{countryCode} {mobile}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
                <Input.OTP length={4} value={otp} onChange={setOtp} size="large" />
              </div>
              <Button type="primary" block loading={sendingOtp} onClick={handleVerifyOtp}
                disabled={otp.length !== 4}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a', height: 48, fontSize: 16, borderRadius: 10, fontWeight: 600 }}>
                Verify Number
              </Button>
              <div style={{ textAlign: 'center', marginTop: 14 }}>
                <Button type="link" loading={sendingOtp} onClick={handleResend} style={{ color: '#1a6b2a' }}>
                  Resend OTP
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 2: details ── */}
          {step === 2 && (
            <Form form={detailsForm} layout="vertical" onFinish={handleSignup} size="large">
              <Form.Item label={t('name')} name="name" rules={[{ required: true, message: 'Enter your name' }]}>
                <Input prefix={<UserOutlined style={{ color: '#ccc' }} />} placeholder="Full name" style={{ borderRadius: 10 }} />
              </Form.Item>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Form.Item label={t('state')} name="state" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Gujarat" style={{ borderRadius: 10 }} />
                </Form.Item>
                <Form.Item label={t('city')} name="city" rules={[{ required: true }]}>
                  <Input placeholder="e.g. Ahmedabad" style={{ borderRadius: 10 }} />
                </Form.Item>
              </div>

              <Form.Item label="Password" name="password"
                rules={[{ required: true, message: 'Enter a password' }, { min: 6, message: 'Min 6 characters' }]}>
                <Input.Password prefix={<LockOutlined style={{ color: '#ccc' }} />} placeholder="Create a password" style={{ borderRadius: 10 }} />
              </Form.Item>

              <Form.Item label="Confirm Password" name="confirm_password"
                dependencies={['password']}
                rules={[
                  { required: true, message: 'Confirm your password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) return Promise.resolve();
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}>
                <Input.Password prefix={<LockOutlined style={{ color: '#ccc' }} />} placeholder="Repeat password" style={{ borderRadius: 10 }} />
              </Form.Item>

              <Form.Item label={t('language')} name="lang" initialValue="en">
                <Select options={SUPPORTED_LANGS.map((l) => ({ value: l.code, label: l.label }))} />
              </Form.Item>

              <Form.Item label={t('photo')} style={{ marginBottom: 20 }}>
                <Upload beforeUpload={(file) => { setPhotoFile(file); return false; }} maxCount={1} accept="image/*">
                  <Button icon={<UploadOutlined />}>{t('upload_photo')}</Button>
                </Upload>
              </Form.Item>

              <Button type="primary" htmlType="submit" block loading={submitting}
                style={{ background: '#1a6b2a', borderColor: '#1a6b2a', height: 48, fontSize: 16, borderRadius: 10, fontWeight: 600 }}>
                Create Account
              </Button>
            </Form>
          )}

          <Divider style={{ margin: '24px 0' }}>
            <Text type="secondary" style={{ fontSize: 13 }}>or</Text>
          </Divider>
          <div style={{ textAlign: 'center' }}>
            <Text type="secondary">Already have an account? </Text>
            <Link to="/login" style={{ color: '#1a6b2a', fontWeight: 600 }}>{t('login')}</Link>
          </div>
        </div>

        {/* ── Right: Hero ── */}
        <HeroPanel />
      </div>
    </>
  );
}
